import { useState, useRef, useMemo, useEffect, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

import useAppStore from "../store/useAppStore";
import useLayoutStore from "../store/useLayoutStore";
import useAuthStore from "../store/useAuthStore";

import ProfileModal from "./elements/ProfileModal";
import ChangeShiftStatusModal from "./elements/ChangeShiftStatusModal";
import NotificationModal from "./elements/NotificationModal";
import ShiftConfirmationModal from "./elements/ShiftConfirmationModal";

// Vrednosti po sidebarSizeTier-u (useLayoutStore) — prate @media blokove u CSS-u
const SIDEBAR_WIDTHS = [84, 84, 112, 136];
const EXPANDED_SIDEBAR_WIDTHS = [300, 300, 348, 390];
const EXPANDED_BUTTON_WIDTHS = [240, 240, 288, 330];
const NAV_BUTTON_WIDTHS = [50, 56, 68, 80];
const PROFILE_BUTTON_WIDTHS = [44, 50, 62, 72];
// Hamburger: pomeraj udesno kad se sidebar rasiri i (visina - debljina sipke) / 2 za X
const HAMBURGER_EXPANDED_OFFSETS = [114, 114, 134, 152];
const HAMBURGER_BAR_OFFSETS = [10.5, 10.5, 13.5, 15.5];

const Sidebar = () => {
    const setActiveSection = useAppStore(state => state.setActiveSection);
    const isExpanded = useAppStore(state => state.isSidebarExpanded)
    const workCheckInToast = useAppStore(state => state.workCheckInToast);
    const languageToast = useLayoutStore(state => state.languageToast);
    const selectedShiftConfirmationNotificationId = useAppStore(state => state.selectedShiftConfirmationNotificationId);

    const smallMediumScreen = useLayoutStore(state => state.smallMediumScreen);
    const sidebarSizeTier = useLayoutStore(state => state.sidebarSizeTier);

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const userRole = useAuthStore(state => state.userRole);
    const isAdmin = userRole === 'admin';
    const landingUrl = import.meta.env.VITE_LANDING_URL;
    const sidebarRef = useRef(null);

    const handleButtonsClick = (e) => {
        const button = e.target.closest('button[data-name]');
        if (!button) return
        const name = button.getAttribute('data-name');
        setActiveSection(name);
    };

    const sidebarWidth = smallMediumScreen ? 59 : SIDEBAR_WIDTHS[sidebarSizeTier];
    const buttonWidth = smallMediumScreen ? 49 : NAV_BUTTON_WIDTHS[sidebarSizeTier];
    // Tier 0 pokriva i mobilni, jer tier 1 pocinje tek od 995px
    const profileWidth = PROFILE_BUTTON_WIDTHS[sidebarSizeTier];
    const expandedWidth = EXPANDED_SIDEBAR_WIDTHS[sidebarSizeTier];
    const expandedButtonWidth = EXPANDED_BUTTON_WIDTHS[sidebarSizeTier];

    // Filtriranje buttons na osnovu userRole
    const visibleButtons = useMemo(() => {
        return buttons.filter(btn => !btn.adminOnly || isAdmin);
    }, [isAdmin]);


    return (
        <motion.div
            ref={sidebarRef}
            className={`sidebar-container ${isDarkTheme ? 'sb-dark-1' : 'sb-white-1'}`}
            style={{ willChange: 'width' }}
            animate={{ width: isExpanded ? expandedWidth : sidebarWidth }}
            transition={{ duration: isExpanded ? 0 : 0.14, type: "tween" }}
        >
            <div className="sidebar-top">
                <HamburgerMenuButton
                    expandedOffsetX={HAMBURGER_EXPANDED_OFFSETS[sidebarSizeTier]}
                    barOffsetY={HAMBURGER_BAR_OFFSETS[sidebarSizeTier]}
                />
                <motion.button
                    className="sidebar-portir-btn"
                    animate={{ width: isExpanded ? expandedButtonWidth : buttonWidth }}
                    transition={{ duration: 0.08, ease: "easeOut", delay: isExpanded ? 0.04 : 0 }}
                    style={{ willChange: 'width' }}
                    // style={{ transform: `translateX(${isExpanded ? '0' : '1px'})` }}
                    onClick={() => window.location.assign(landingUrl)}
                >
                    <img src={isDarkTheme ? "./sidebar/PortirMunjaBela.svg" : "./sidebar/NormMunjica.svg"}
                        alt="Portir Logo" className="sidebar-portir-logo" />
                    <AnimatePresence>
                        {isExpanded && (
                            <motion.img
                                className="portir-logo-text-img"
                                // src="./sidebar/portirLogoText.svg"
                                src={isDarkTheme ? "./sidebar/PortirLogoTextBeo.svg" : "./sidebar/PortirLogoText.svg"}
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{ duration: 0.04, ease: "easeOut", delay: 0.04 }}
                                exit={{ scaleX: 0, scaleY: 0, opacity: 0, transition: { duration: 0.14, ease: "easeOut", delay: 0.04 } }}
                            >
                            </motion.img>
                        )}
                    </AnimatePresence>
                </motion.button>
                {/* <motion.div className="sb-search-container">
                    <motion.div
                        className="sidebar-search-icon-div"
                        animate={{ x: isExpanded ? 100 : 0, scale: isExpanded ? 0.84 : 1 }}
                        transition={{ duration: 0.08, ease: "easeOut", delay: 0.1 }}
                    >
                        <img
                            src="./sidebar/Pretrazivanje.svg"
                            alt="Search Icon"
                            // className="sidebar-search-icon" 
                            className={`sidebar-search-icon ${isDarkTheme ? 'dark-filter' : ''}`}
                        />
                    </motion.div>
                    <AnimatePresence>
                        {isExpanded && (
                            <motion.input
                                className={`sidebar-search-input ${isDarkTheme ? 'dark-sb-input' : ''}`}
                                placeholder="Pretraži..."
                                name="search"
                                initial={{ scaleX: 0, opacity: 0.1 }}
                                animate={{ scaleX: 1, opacity: 1 }}
                                transition={{ duration: 0.08, ease: "easeOut", delay: 0.14 }}
                                exit={{ scaleX: 0, opacity: 0, duration: 0.12 }}
                            />
                        )}
                    </AnimatePresence>
                </motion.div> */}

            </div>
            <div className="sidebar-divider"></div>
            <div className="sidebar-bottom" onClick={handleButtonsClick}>
                {visibleButtons.map((btn, index) => (
                    <SidebarButton
                        key={btn.name}
                        {...btn}
                        index={index}
                        width={buttonWidth}
                        expandedWidth={expandedButtonWidth}
                    />
                ))}
            </div>
            <div className="sidebar-bottom-menu-container">
                <NotificationButton width={buttonWidth} expandedWidth={expandedButtonWidth} />
                <ProfileButton width={profileWidth} expandedWidth={expandedButtonWidth} />
            </div>
            <AnimatePresence>
                {workCheckInToast && <WorkCheckInToast key="work-checkin-toast" toastData={workCheckInToast} />}
            </AnimatePresence>
            <AnimatePresence>
                {languageToast && <LanguageChangedToast key="language-changed-toast" toastData={languageToast} />}
            </AnimatePresence>
            <AnimatePresence>
                {selectedShiftConfirmationNotificationId && <ShiftConfirmationModal key="shift-confirmation-modal" />}
            </AnimatePresence>

        </motion.div>
    );
}

export default Sidebar;

const NotificationButton = ({ width, expandedWidth }) => {
    const { t } = useTranslation("sidebar");
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const loading = useAppStore(state => state.loading)
    const closeSidebar = useAppStore(state => state.closeSidebar);
    const isExpanded = useAppStore(state => state.isSidebarExpanded)
    const unreadNotificationCount = useAppStore(state => state.unreadNotificationCount)
    const isNotificationModalOpen = useAppStore(state => state.isNotificationModalOpen)
    const setIsNotificationModalOpen = useAppStore(state => state.setIsNotificationModalOpen)

    const isTouchDevice = useLayoutStore(state => state.isTouchDevice);

    const [isHovered, setIsHovered] = useState(false)

    const handleClick = () => {
        if (loading) return;
        closeSidebar(false);
        setIsNotificationModalOpen(true);
    };

    return (
        <div
            className="sidebar-btn-wrapper"

        >
            <motion.button
                className="notification-button sidebar-bottom-button"
                animate={{ width: isExpanded ? expandedWidth : width }}
                transition={{ duration: 0.08, ease: "easeOut", delay: isExpanded ? 0.24 : 0 }}
                onClick={handleClick}
                style={{ position: 'relative', backgroundColor: 'transparent', willChange: 'width' }}
                onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
                onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
            >
                <div style={{ position: 'relative' }}>
                    <img
                        className={`notification-icon ${isDarkTheme ? 'dark-filter' : ''}`}
                        src="./cards/Zvonce.svg"
                        alt=""
                    />
                    <AnimatePresence mode="wait">
                        {unreadNotificationCount > 0 && (
                            <motion.div
                                key={unreadNotificationCount}
                                className="notification-badge"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ duration: 0.14, ease: "easeOut" }}
                            >
                                {unreadNotificationCount > 9 ? '?' : unreadNotificationCount}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <AnimatePresence>
                    {isExpanded && (
                        <motion.span
                            className="sidebar-btn-text"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.14, ease: "easeOut", delay: isExpanded ? 0.24 : 0 }}
                            exit={{ scale: 0, transition: { duration: 0.14, ease: "easeOut", delay: 0.04 } }}
                        >
                            {t("notifications")}
                        </motion.span>
                    )}
                </AnimatePresence>
            </motion.button>
            <AnimatePresence>
                {!isExpanded && isHovered && (
                    <motion.div
                        key={t("notifications")}
                        className={`sidebar-tooltip ${isDarkTheme ? 'tooltip-dark' : 'tooltip-white'}`}
                        initial={{ opacity: 0, x: -14 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -14 }}
                        transition={{ duration: 0.19, ease: "easeOut" }}
                    >
                        {t("notifications")}
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {isNotificationModalOpen && (
                    <NotificationModal
                        key="notification-modal"
                        onClose={() => setIsNotificationModalOpen(false)}
                    />
                )}
            </AnimatePresence>
        </div>
    )
}

const ProfileButton = ({ width, expandedWidth }) => {
    const { t } = useTranslation("sidebar");
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const loading = useAppStore(state => state.loading)
    const isExpanded = useAppStore(state => state.isSidebarExpanded)
    const userId = useAuthStore(state => state.userId)
    const userImage = useAppStore(state => state.employeesData?.[userId]?.image)

    const closeSidebar = useAppStore(state => state.closeSidebar);
    const isWorkPresenceFormOpen = useAppStore(state => state.isWorkPresenceFormOpen);

    const isTouchDevice = useLayoutStore(state => state.isTouchDevice);

    const [isHovered, setIsHovered] = useState(false)

    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleClick = () => {
        if (loading) return;
        closeSidebar(false);
        setIsModalOpen(true);
    };

    const profileImg = useMemo(() => {
        if (loading || !userImage) return null
        const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
        return `${baseImgUrl}/employees/${userImage}`
    }, [loading, userImage])

    return (
        <div
            className="sidebar-btn-wrapper"
        >
            <motion.button
                className="profile-button sidebar-bottom-button"
                animate={{ width: isExpanded ? expandedWidth : width }}
                transition={{ duration: 0.08, ease: "easeOut", delay: isExpanded ? 0.28 : 0 }}
                style={{ willChange: 'width' }}
                onClick={handleClick}
                onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
                onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
            >
                <motion.img
                    key={profileImg || "profile-fallback"}
                    src={profileImg || "/views/login.svg"}
                    crossOrigin="use-credentials"
                    className="profile-button-image"
                    animate={{ x: isExpanded ? 4 : 0 }}
                    transition={{ duration: 0.08, ease: "easeOut", delay: isExpanded ? 0.28 : 0 }}
                    style={{ willChange: 'transform' }}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.classList.add("profile-button-image-error");
                        e.target.src = "/views/login.svg";
                    }}
                />
                <AnimatePresence>
                    {isExpanded && (
                        <motion.span
                            className="sidebar-btn-text"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.14, ease: "easeOut", delay: isExpanded ? 0.28 : 0 }}
                            exit={{ scale: 0, transition: { duration: 0.14, ease: "easeOut", delay: 0 } }}
                        >
                            {t("profile")}
                        </motion.span>
                    )}
                </AnimatePresence>
            </motion.button>
            <AnimatePresence>
                {!isExpanded && isHovered && (
                    <motion.div
                        key={t("profile")}
                        className={`sidebar-tooltip ${isDarkTheme ? 'tooltip-dark' : 'tooltip-white'}`}
                        initial={{ opacity: 0.1, x: -14 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0.1, x: -14 }}
                        transition={{ duration: 0.19, ease: "easeOut" }}
                    >
                        {t("profile")}
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {isModalOpen &&
                    <ProfileModal
                        key="profile-modal"
                        onClose={() => setIsModalOpen(false)}
                    />
                }
            </AnimatePresence>
            <AnimatePresence>
                {isWorkPresenceFormOpen &&
                    <ChangeShiftStatusModal
                        key="change-shift-status-modal"
                    />
                }
            </AnimatePresence>
        </div>
    )
}

const WorkCheckInToast = ({ toastData }) => {
    const { t } = useTranslation("sidebar");
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const setWorkCheckInToast = useAppStore(state => state.setWorkCheckInToast);

    const dismiss = useCallback(() => {
        setWorkCheckInToast(null);
    }, [setWorkCheckInToast]);

    useEffect(() => {
        const timer = setTimeout(dismiss, 50000);
        return () => clearTimeout(timer);
    }, [dismiss]);

    const content = (
        <motion.div
            className="dv-device-toast-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="na-toast-content">
                    <button
                        type="button"
                        className="na-toast-close-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            dismiss();
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <line x1="6.8" y1="6.8" x2="21.2" y2="21.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                            <line x1="21.2" y1="6.8" x2="6.8" y2="21.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        </svg>
                    </button>
                    <h4 className="na-toast-title">{toastData?.title || t("toast.defaultTitle")}</h4>
                    <p className="na-toast-message-text">
                        {toastData?.message || t("toast.defaultMessage")}
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );

    return createPortal(content, document.body);
};

const LanguageChangedToast = ({ toastData }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const setLanguageToast = useLayoutStore(state => state.setLanguageToast);

    const dismiss = useCallback(() => {
        setLanguageToast(null);
    }, [setLanguageToast]);

    useEffect(() => {
        const timer = setTimeout(dismiss, 4500);
        return () => clearTimeout(timer);
    }, [dismiss]);

    const content = (
        <motion.div
            className="dv-device-toast-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="na-toast-content">
                    <button
                        type="button"
                        className="na-toast-close-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            dismiss();
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <line x1="6.8" y1="6.8" x2="21.2" y2="21.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                            <line x1="21.2" y1="6.8" x2="6.8" y2="21.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        </svg>
                    </button>
                    <h4 className="na-toast-title">{toastData?.title}</h4>
                    <p className="na-toast-message-text">
                        {toastData?.message}
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );

    return createPortal(content, document.body);
};


const SidebarTooltip = ({ anchorRef, label, isDarkTheme }) => {
    const readPosition = useCallback(() => {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (!rect) return null;
        // Anchor je sirok koliko i sadrzaj sidebar-a, pa se offset skalira sa njegovom sirinom
        return { top: rect.top + rect.height / 2, left: rect.right + 9 };
    }, [anchorRef]);

    const [position, setPosition] = useState(readPosition);

    useLayoutEffect(() => {
        const update = () => setPosition(readPosition());
        update();

        const scrollContainer = anchorRef.current?.parentElement;
        window.addEventListener("resize", update);
        scrollContainer?.addEventListener("scroll", update);
        return () => {
            window.removeEventListener("resize", update);
            scrollContainer?.removeEventListener("scroll", update);
        };
    }, [anchorRef, readPosition]);

    if (!position) return null;

    return createPortal(
        <motion.div
            className={`sidebar-tooltip sidebar-tooltip-fixed ${isDarkTheme ? 'tooltip-dark' : 'tooltip-white'}`}
            style={{ top: position.top, left: position.left }}
            initial={{ opacity: 0.1, x: -14, y: "-50%" }}
            animate={{ opacity: 1, x: 0, y: "-50%" }}
            exit={{ opacity: 0.1, x: -14, y: "-50%" }}
            transition={{ duration: 0.19, ease: "easeOut" }}
        >
            {label}
        </motion.div>,
        document.body
    );
};


const SidebarButton = ({ name, icon, alt, index, width, expandedWidth }) => {
    const { t } = useTranslation("sidebar");
    const activeSection = useAppStore(state => state.activeSection);
    const isExpanded = useAppStore(state => state.isSidebarExpanded)

    const isTouchDevice = useLayoutStore(state => state.isTouchDevice);
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const activeClass = isDarkTheme ? 'dark-active' : 'white-active'

    const [isHovered, setIsHovered] = useState(false)
    const wrapperRef = useRef(null)
    const labelKeyBySection = {
        Prisutnost: "nav.presence",
        Statistika: "nav.stats",
        "Karnet-obrazac": "nav.karnet",
        Kalendar: "nav.calendar",
        "Status uređaja": "nav.devices",
        "Podešavanja": "nav.settings",
    }
    const label = labelKeyBySection[name] ? t(labelKeyBySection[name]) : name

    return (
        <div
            ref={wrapperRef}
            className="sidebar-btn-wrapper"
            onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
            onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
        >
            <motion.button
                key={name}
                data-name={name}
                className={activeSection === name ? activeClass : ""}
                animate={{ width: isExpanded ? expandedWidth : width }}
                transition={{ duration: 0.08, ease: "easeOut", delay: index * 0.04 }}
                style={{ willChange: 'width, height' }}
            >
                {/* <img src={icon} alt={alt} className="sidebar-icon" /> */}
                <img src={icon} alt="" className={`sidebar-icon ${isDarkTheme ? 'dark-filter' : ''}`} />

                <AnimatePresence>
                    {isExpanded && (
                        <motion.span
                            className="sidebar-btn-text"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.14, ease: "easeOut", delay: index * 0.04 }}
                            exit={{ scale: 0 }}
                        >
                            {label}
                        </motion.span>
                    )}
                </AnimatePresence>
            </motion.button>
            <AnimatePresence>
                {!isExpanded && isHovered && (
                    <SidebarTooltip
                        key={label}
                        anchorRef={wrapperRef}
                        label={label}
                        isDarkTheme={isDarkTheme}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};


const HamburgerMenuButton = ({ expandedOffsetX, barOffsetY }) => {
    const isExpanded = useAppStore(state => state.isSidebarExpanded);
    const toggleSidebar = useAppStore(state => state.toggleSidebar);

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    return (
        <motion.div
            className="hamburger-menu-button-container"
            animate={{
                x: isExpanded ? expandedOffsetX : 0
            }}
            transition={{
                duration: 0.16,
                ease: "easeOut",
                delay: isExpanded ? 0.2 : 0.14
            }}
            style={{ willChange: 'transform' }}
        >
            <div className="hamburger-menu-button" onClick={toggleSidebar}>
                <motion.span
                    className={`hamBar hb1 ${isDarkTheme ? 'bg-dark' : 'bg-white'}`}
                    animate={isExpanded ? { rotate: 45, y: barOffsetY } : { rotate: 0, y: 0 }}
                    transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                        delay: isExpanded ? 0 : 0.1
                    }}
                />
                <motion.span
                    className={`hamBar hb2 ${isDarkTheme ? 'bg-dark' : 'bg-white'}`}
                    animate={isExpanded ? { rotate: -45 } : { rotate: 0 }}
                    transition={{ duration: 0.2, delay: 0.1 }}
                />
                <motion.span
                    className={`hamBar hb3 ${isDarkTheme ? 'bg-dark' : 'bg-white'}`}
                    animate={isExpanded ? { rotate: -45, y: -barOffsetY } : { rotate: 0, y: 0 }}
                    transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                        delay: isExpanded ? 0.04 : 0
                    }}
                />
            </div>
        </motion.div>
    )
}

const buttons = [
    {
        name: "Prisutnost",
        icon: "./sidebar/Prisutnost.svg",
        alt: "Attendance Icon",
        adminOnly: false,
    },
    {
        name: "Statistika",
        icon: "./sidebar/Statistika.svg",
        alt: "Statistics Icon",
        adminOnly: false,
    },
    {
        name: "Karnet-obrazac",
        icon: "./sidebar/Karnet.svg",
        alt: "Report Card Icon",
        adminOnly: true,
    },
    {
        name: "Kalendar",
        icon: "./sidebar/Kalendar.svg",
        alt: "Calendar Icon",
        adminOnly: false,
    },
    {
        name: "Status uređaja",
        icon: "./sidebar/Uredjaji.svg",
        alt: "Devices Icon",
        adminOnly: false,
    },
    {
        name: "Podešavanja",
        icon: "./sidebar/Podesavanja.svg",
        alt: "Settings Icon",
        adminOnly: true,
    },
];